import { z } from "zod";
import { zImageReference } from "./imageReferenceSchema";
import { zLocationSpan } from "./stageMovement";

const finite = z.number().finite();
/** A standing position painted into a backdrop, separate from the world place's identity. */
export const zBackdropLocation = z
  .object({
    id: z.string().min(1).max(200),
    description: z.string(),
    frame: z.enum(["backdrop", "band", "viewport"]),
    band: z.enum(["backstage", "background", "onstage", "forestage"]),
    x: finite.min(0).max(1).optional(),
    span: zLocationSpan.optional(),
    footY: finite,
    size: finite.positive(),
    angle: finite.optional(),
    brightness: finite.min(0).max(1).optional(),
    parallax: finite.optional(),
    role: z.string().optional(),
    surface: z.string().optional(),
    source: z.string().optional(),
    measuredFit: z.string().optional(),
    aliasOf: z.string().optional(),
  })
  .strip();
export type BackdropLocation = z.infer<typeof zBackdropLocation>;
export const zBackdropLighting = z
  .object({
    stage: finite.min(0).max(1),
    gallery: finite.min(0).max(1),
    galleryBackground: z.string(),
  })
  .strip();
export type BackdropLighting = z.infer<typeof zBackdropLighting>;

/** App-owned experiences only. Saved stories cannot embed arbitrary HTML or URLs. */
export const zInteractiveBackdrop = z.object({
  kind: z.literal("prize-machine"),
  title: z.string().max(100).optional(),
  initialMode: z.enum(["mixed", "dressup", "music", "adventure"]).optional(),
}).strict();
export type InteractiveBackdrop = z.infer<typeof zInteractiveBackdrop>;

/** IDs select places. ImageReference identifies their artwork; IDs never imply filenames. */
export const zBackdrop = z
  .object({
    id: z.string().min(1).max(200),
    name: z.string().max(200).optional(),
    description: z.string(),
    image: zImageReference.optional(),
    imageVariations: z.record(zImageReference).optional(),
    interactive: zInteractiveBackdrop.optional(),
    art: z.object({ width: finite.positive(), height: finite.positive() }).optional(),
    locations: z.array(zBackdropLocation).max(100).default([]),
    lighting: zBackdropLighting.default({ stage: 1, gallery: 1, galleryBackground: "#FDF8F3" }),
    fit: z.enum(["cover", "contain"]).optional(),
    layout: z.enum(["fullscreen", "stage", "compact", "standard", "performersLarge", "performersOnly"]).optional(),
    castScale: finite.positive().max(20).optional(),
  })
  .strip();
export type Backdrop = z.infer<typeof zBackdrop>;

/** Account/world resource mapping; a story can reuse the backdrop without guessing from its name. */
export const zWorldPlace = z
  .object({
    entityId: z.string().min(1).max(100),
    backdropId: z.string().min(1).max(200),
    backdrop: zBackdrop,
  })
  .strip();
export type WorldPlace = z.infer<typeof zWorldPlace>;
